import React, { ReactElement, useEffect, useState } from "react";
import {
    Nav,
    NavDropdown,
    Navbar,
} from "react-bootstrap";
import { AiOutlineGift } from "react-icons/ai";
import { BiBookHeart } from "react-icons/bi";
import { BsGearFill, BsPlusSquare } from "react-icons/bs";
import { FaChartPie, FaInbox, FaUsers } from "react-icons/fa";
import { FiHelpCircle } from "react-icons/fi";
import {
    IoPerson, IoTicketSharp,
} from "react-icons/io5";
import {
    MdOutlineMarkChatUnread, MdOutlineTipsAndUpdates,
} from "react-icons/md";
import { TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import { Permission, PortalEventType } from "../../__generated__/api";
import logo from "../../assets/img/logo-white.svg";
import useCreateTicket from "../../contexts/CreateTicketContext";
import { useHelpContext } from "../../contexts/HelpContext";
import usePortal from "../../contexts/PortalContext";
import useEvents from "../../contexts/PortalSubscriptionContext";
import useRouting, { NavLink, isRouteMatch } from "../../contexts/Routing";
import useCurrentSession from "../../contexts/UserContext";
import useUpdatesContext from "../../contexts/updates/UpdatesContext";
import Avatar from "../avatar/Avatar";
import CodeBlock from "../code-block/CodeBlock";
import Confirm from "../confirm/Confirm";
import Help from "../help/Help";
import "./Navigation.css";

/** Navigation Bar */

const ICON_SIZE = "25px";

const KEY_NAVIGATION = "navigation";

const FEATURE_SHOW_PAGE_HELP = "frontend.show.page.help";
const FEATURE_SHOW_REPORTS = "frontend.show.page.reports";

interface NavigationSettings {
    isExpanded: boolean;
}

export const FEATURE_DARKER = "frontend.design.darker";

export default function Navigation({ shouldDisplayOnSide }: {
    shouldDisplayOnSide?: boolean;
}): ReactElement {
    const {
        isLoggedIn, user, logout, current: currentSession, clearCache,
    } = useCurrentSession();

    const helpContext = useHelpContext();
    const {
        permissions, featureFlags, id: portalId, invalidate: invalidatePortal,
    } = usePortal();

    const [settings, setSettings] = useState<NavigationSettings>(() => {
        try {
            let current = localStorage.getItem(KEY_NAVIGATION);
            if (!current) {
                current = JSON.stringify({
                    isExpanded: false,
                });
                localStorage.setItem(KEY_NAVIGATION, current);
            }
            return JSON.parse(current) as NavigationSettings;
        } catch (err) {
            const state = {
                isExpanded: false,
            };
            localStorage.setItem(KEY_NAVIGATION, JSON.stringify(state));
            return state as NavigationSettings;
        }
    });
    useEffect(() => {
        if (settings) {
            localStorage.setItem(KEY_NAVIGATION, JSON.stringify(settings));
        }
    }, [settings]);

    const updates = useUpdatesContext();

    const { createHistoryPush, Paths } = useRouting();

    const isRouteAgent = isRouteMatch({ path: Paths.AGENT, exact: false, strict: false });
    const isRouteTickets = isRouteMatch({ path: Paths.TICKETS, exact: false, strict: false });

    const [hasMessage, setHasMessages] = useState(false);
    const createTicket = useCreateTicket();

    const [showDebug, setShowDebug] = useState(false);

    useEffect(() => {
        if (hasMessage && (isRouteAgent || isRouteTickets)) {
            setHasMessages(false);
        }
    }, [isRouteAgent, isRouteTickets, hasMessage]);

    useEvents(api => {
        api.on(PortalEventType.MessageNew, 0, () => {
            setHasMessages(true);
        });
        api.on(PortalEventType.ConversationNew, 0, () => {
            setHasMessages(true);
        });
    });

    const navbarClasses = [];
    if (shouldDisplayOnSide) {
        navbarClasses.push("flex-grow-1 d-none d-lg-block");
        if (settings.isExpanded) {
            navbarClasses.push("");
        } else {
            navbarClasses.push("w-100 justify-content-between d-flex mx-0");
        }
    }

    const ticketsLink = (permissions.has(Permission.ChatsModify) ? (
        <NavLink
            to={Paths.AGENT}
            className={`navbar-link position-relative d-block ${
                shouldDisplayOnSide ? "mb-3  w-100 text-center text-lg-start" : "mx-lg-2 mx-2 px-1 "}`}
        >
            {shouldDisplayOnSide && <FaInbox className="me-0 d-inline" size={ICON_SIZE} />}
            <span className={shouldDisplayOnSide
                ? "d-inline ms-2 nav-label"
                : ""}
            >
                Tickets
            </span>

            {hasMessage && (
                <span
                    className={"position-absolute top-0 start-75 translate-middle "
                        + "bg-danger badge p-1 rounded-circle"}
                >
                    <MdOutlineMarkChatUnread />
                    <span className="visually-hidden">New alerts</span>
                </span>
            )}

        </NavLink>
    ) : null);

    const answersLink = (permissions.has(Permission.ArticleReadPublic) ? (
        <NavLink
            to={Paths.ANSWERS_LIST}
            className={`navbar-link  ${shouldDisplayOnSide ? "mb-3 w-100 text-center text-lg-start" : "mx-lg-0 mx-2 "}`}
        >
            {shouldDisplayOnSide && <BiBookHeart className="me-0 d-inline" size={ICON_SIZE} />}
            <span className={shouldDisplayOnSide
                ? "d-inline ms-2 nav-label"
                : ""}
            >
                Answers
            </span>
        </NavLink>
    ) : null);

    return (
        <>
            {showDebug && (
                <Confirm shouldShow onHide={() => setShowDebug(false)}>
                    <div>
                        <h5>Debugging Info</h5>
                        <small>
                            This information is useful when talking to support.
                        </small>

                        <div>
                            <button
                                onClick={() => {
                                    clearCache();
                                    invalidatePortal();
                                }}
                                type="button"
                                className="btn btn-none"
                            >
                                Clear Cache
                            </button>
                        </div>

                        <div className="mb-2">
                            <small className="text-light">Please send the following information to support</small>
                            <div>
                                <CodeBlock
                                    code={
                                        Object.entries({
                                            "User Id": user?.id,
                                            "Session Id": currentSession?.id,
                                            "Portal Id": portalId,
                                            Group: user?.group,
                                            Role: user?.role,
                                            "User Groups": user?.userGroups?.toIdList("id"),
                                            Permissions: Array.from(permissions),
                                            "Feature Flags": Array.from(featureFlags),
                                        })
                                            .filter(([, value]) => value != null)
                                            .map(([key, value]) => {
                                                const formatted = Array.isArray(value) ? `\n\t${value.join(",\n\t")}` : value;
                                                return `${key}: ${formatted}`;
                                            })
                                            .join("\n")
                                    }
                                    isCopyable
                                    size="sm"
                                    alert="light"
                                />
                            </div>
                        </div>

                    </div>
                </Confirm>
            )}
            <Navbar
                className={`nav ${featureFlags.has(FEATURE_DARKER) ? "bg-blue-dark" : "bg-gradient"} p-0 ${
                    shouldDisplayOnSide
                        ? `nav-side  flex-lg-column position-absolute position-relative-lg rounded-bottom-end-sm`
                        : "ps-3 "
                } ${settings.isExpanded ? "expanded " : ""}`}
                expand="lg"
            >
                <div className={shouldDisplayOnSide
                    ? "d-none d-lg-flex align-items-center  justify-content-between my-2 mx-0 w-100"
                    : ""}
                >
                    <Navbar.Brand
                        className={shouldDisplayOnSide ? navbarClasses.join(" ") : "m-0 p-0 me-2 my-2 d-block"}
                        onClick={shouldDisplayOnSide
                            ? () => setSettings({ ...settings, isExpanded: !settings.isExpanded })
                            : undefined}
                    >
                        <img
                            src={logo}
                            alt="Foqal logo"
                            className={`mx-auto ${shouldDisplayOnSide ? "nav-logo" : "nav-logo-legacy"}`}
                        />
                    </Navbar.Brand>
                    {shouldDisplayOnSide && (
                        <button
                            type="button"
                            className={`btn btn-none d-none text-blue-light expander me-2 p-1 ${
                                settings.isExpanded ? "d-lg-block" : ""}`}
                            onClick={() => setSettings({ ...settings, isExpanded: !settings.isExpanded })}
                        >
                            <TbLayoutSidebarLeftCollapse size="20px" />
                        </button>
                    )}

                </div>

                <Navbar.Toggle aria-controls="responsive-navbar-nav" />

                <Navbar.Collapse id="responsive-navbar-nav" className="w-100">
                    <Nav className={`nav-responsive p-0 d-flex ${shouldDisplayOnSide
                        ? "h-100 flex-lg-column justify-content-between align-items-start"
                        : `w-100 justify-content-between align-items-center`}`}
                    >
                        <div className={`d-flex align-items-top ${
                            shouldDisplayOnSide
                                ? "flex-column align-items-center w-100 align-items-lg-start"
                                : "align-items-center"
                        }`}
                        >

                            {shouldDisplayOnSide ? (
                                <>
                                    {ticketsLink}
                                    {answersLink}
                                </>
                            ) : (
                                <>
                                    {answersLink}
                                    {ticketsLink}
                                </>
                            )}

                            {permissions.has(Permission.UsersCreate) && (

                                <NavLink
                                    to={Paths.CONTACTS}
                                    className={`navbar-link ${shouldDisplayOnSide
                                        ? "mb-3 w-100 text-center text-lg-start"
                                        : "mx-lg-0 mx-2 "}`}
                                >
                                    {shouldDisplayOnSide && <FaUsers className="me-0 d-inline" size={ICON_SIZE} />}
                                    <span className={shouldDisplayOnSide
                                        ? "d-inline ms-2 nav-label"
                                        : ""}
                                    >
                                        Contacts
                                    </span>

                                </NavLink>
                            )}

                            {featureFlags.has(FEATURE_SHOW_REPORTS) && permissions.has(Permission.Reports) && (

                                <NavLink
                                    to={Paths.REPORTS}
                                    className={`navbar-link ${shouldDisplayOnSide
                                        ? "mb-3 w-100 text-center text-lg-start"
                                        : "mx-lg-0 mx-2 "}`}
                                >
                                    {shouldDisplayOnSide && <FaChartPie className="me-0 d-inline" size={ICON_SIZE} />}
                                    <span className={shouldDisplayOnSide
                                        ? "d-inline ms-2 nav-label"
                                        : ""}
                                    >
                                        Reports
                                    </span>

                                </NavLink>
                            )}

                            {!permissions.has(Permission.ChatsModify)
                                && (permissions.has(Permission.TicketsReadSelf) || permissions.has(Permission.TicketsRead))
                                && (
                                    <NavLink
                                        to={Paths.TICKETS}
                                        className="navbar-link position-relative"
                                    >
                                        {shouldDisplayOnSide && <IoTicketSharp className="me-2 me-lg-0" size={ICON_SIZE} />}
                                        <span className={shouldDisplayOnSide
                                            ? "d-inline ms-2 nav-label"
                                            : "d-block d-lg-none"}
                                        >
                                            My Tickets
                                        </span>
                                        {hasMessage && (
                                            <span
                                                className={"position-absolute top-0 start-100 translate-middle "
                                                    + "bg-danger badge p-1 rounded-circle"}
                                            >
                                                <MdOutlineMarkChatUnread />
                                                <span className="visually-hidden">New tickets</span>
                                            </span>
                                        )}
                                    </NavLink>
                                )}

                        </div>

                        <div className={`d-flex  ${shouldDisplayOnSide
                            ? `flex-column align-items-start ${settings.isExpanded
                                ? "align-items-start"
                                : "align-items-center w-100"}`
                            : ` flex-column flex-lg-row`}`}
                        >
                            {featureFlags.has(FEATURE_SHOW_PAGE_HELP) && helpContext.getArticleId() && (
                                <Help
                                    articleId={helpContext.getArticleId()}
                                    buttonClassName={`navbar-link btn btn-none text-light position-relative ${
                                        shouldDisplayOnSide ? "py-0 mb-2" : "p-0 mx-3"}`}
                                    button={(
                                        <>
                                            <FiHelpCircle className="fs-5" />
                                            <span className={
                                                shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                            }
                                            >
                                                Help
                                            </span>
                                        </>
                                    )}
                                />
                            )}

                            {(permissions.has(Permission.ChatsModifyAll)) && (
                                <button
                                    type="button"
                                    onClick={() => updates.show()}
                                    className={`navbar-link btn btn-none text-light position-relative ${
                                        shouldDisplayOnSide ? "py-0" : "p-0 mx-3 my-2"}`}
                                >

                                    <AiOutlineGift className="fs-5" />
                                    <span className={
                                        shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                    }
                                    >
                                        Updates
                                    </span>
                                    {updates.unreadCount() > 0 && (
                                        <span
                                            className={"position-absolute top-0 start-75 translate-middle "
                                                + "bg-button-hover badge p-1 rounded-circle"}
                                        >
                                            <MdOutlineTipsAndUpdates />
                                            <span className="visually-hidden">New Updates</span>
                                        </span>
                                    )}
                                </button>
                            )}
                            {(permissions.has(Permission.ArticleModify)
                                || permissions.has(Permission.TicketsModify)
                                || permissions.has(Permission.TicketCreatePublic)
                                || permissions.has(Permission.TicketCreateGuest)
                            )
                                && (
                                    <NavDropdown
                                        data-test="create-dropdown"
                                        drop={shouldDisplayOnSide ? "up" : undefined}
                                        align={shouldDisplayOnSide ? "start" : "end"}
                                        title={(
                                            <span>
                                                <BsPlusSquare className="fs-5" />
                                                <span className={
                                                    shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2"
                                                }
                                                >
                                                    Create
                                                </span>
                                            </span>
                                        )}
                                        id="collapsible-nav-dropdown"
                                        bsPrefix="navbar-link"
                                        className={`navdropdown-link m-lg-0 m-2 menu-small ${
                                            !shouldDisplayOnSide ? "p-0 align-self-center" : ""
                                        }`}
                                    >
                                        {permissions.has(Permission.ArticleModify) && (
                                            <NavDropdown.Item
                                                eventKey="3"
                                                data-test="new-answer"
                                                onClick={createHistoryPush(Paths.ANSWERS_NEW)}
                                            >
                                                Article
                                            </NavDropdown.Item>
                                        )}
                                        {permissions.has(Permission.TicketsModify) && (
                                            <NavDropdown.Item
                                                eventKey="4"
                                                data-test="agent-compose"
                                                onClick={createHistoryPush(Paths.AGENT_COMPOSE)}
                                            >
                                                Compose Message
                                            </NavDropdown.Item>
                                        )}
                                        {(
                                            permissions.has(Permission.TicketCreatePublic)
                                            || permissions.has(Permission.TicketCreateGuest)
                                            || permissions.has(Permission.ChatsModify)
                                        )
                                            && (
                                                <NavDropdown.Item
                                                    eventKey="5"
                                                    data-test="ticket-create"
                                                    onClick={() => createTicket.show()}
                                                >
                                                    Request
                                                </NavDropdown.Item>
                                            )}

                                    </NavDropdown>

                                )}
                            {(permissions.has(Permission.Administrator)) && (
                                <NavLink
                                    to={Paths.ADMINISTRATOR}
                                    className={`navbar-link ${shouldDisplayOnSide ? "" : "m-lg-0 mx-5 align-self-center"}`}
                                >
                                    <BsGearFill className="fs-5" />
                                    <span className={
                                        shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                    }
                                    >
                                        Settings
                                    </span>
                                </NavLink>
                            )}
                            {(!isLoggedIn || !user) && (
                                <NavLink
                                    to={Paths.LOGIN}
                                    className="navbar-link text-center"
                                >
                                    Sign In
                                </NavLink>
                            )}

                            {(isLoggedIn && user) && (
                                <NavDropdown
                                    data-test="user-account"
                                    drop={shouldDisplayOnSide ? "up" : undefined}
                                    align={shouldDisplayOnSide ? "start" : "end"}
                                    title={(
                                        <>
                                            <Avatar
                                                src=""
                                                variant="sm"
                                                defaultIcon={
                                                    <IoPerson className={!shouldDisplayOnSide ? "me-2" : ""} />
                                                }
                                            />

                                            <span className={
                                                shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                            }
                                            >
                                                Profile
                                            </span>

                                        </>
                                    )}
                                    id="collapsible-nav-dropdown"
                                    bsPrefix="navbar-link"
                                    className="navdropdown-link menu-small"
                                >
                                    <NavDropdown.Header className="d-flex flex-column align-items-end">
                                        <div className="fs-6">
                                            Welcome
                                            {" "}
                                            {user.name}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowDebug(true)}
                                            className="btn btn-none text-light fs-6 p-0"
                                        >
                                            <small>
                                                Support Info
                                            </small>
                                        </button>
                                    </NavDropdown.Header>

                                    <NavDropdown.Divider className="mx-3 mb-4 mt-0" />

                                    {permissions.has(Permission.SuperAdmin) && (
                                        <NavLink
                                            to={Paths.SITE_ADMINISTRATOR}
                                            className="text-end dropdown-item"
                                        >
                                            Site Admin
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModifyAll) && (
                                        <NavLink
                                            to={Paths.ACCOUNT_PROFLE}
                                            className="text-end dropdown-item"
                                        >
                                            My Profile
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModify) && (
                                        <NavLink
                                            to={Paths.TICKETS}
                                            className="text-end dropdown-item"
                                        >
                                            My Tickets
                                        </NavLink>
                                    )}

                                    {/* <NavDropdown.Item
                                        data-test="profile"
                                        eventKey="1"
                                        onClick={createHistoryPush(Paths.ACCOUNT_PROFLE)}
                                        className="text-end"
                                    >
                                        Profile
                                    </NavDropdown.Item> */}

                                    <NavDropdown.Item
                                        onClick={logout}
                                        className="text-end"
                                    >
                                        <div data-test="logout">
                                            Logout
                                        </div>
                                    </NavDropdown.Item>
                                </NavDropdown>
                            )}
                        </div>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
        </>
    );
}
Navigation.defaultProps = {
    shouldDisplayOnSide: false,
};
